import { type ReactNode, useState } from "react";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { MonetaryInput } from "@design-system/MonetaryInput/MonetaryInput";
import type { InputSize } from "@helpers/form";
import type { FieldPath, FieldPathEmptyValue } from "./fieldPath.types";
import { pickChromeProps } from "./kitChromeProps";
import { type KitClearableProps, toClearTarget } from "./kitClearable";
import type { KitFieldProps } from "./kitFieldProps.types";

type MoneyFieldValue = number | null | undefined;

/** Paths of `Values` that hold a currency amount. */
export type MoneyFieldPath<Values> = FieldPath<Values, MoneyFieldValue, number>;

export type MoneyFieldProps<Values, Name extends string> = KitFieldProps<
    Name,
    HTMLInputElement,
    number | FieldPathEmptyValue<Values, Name>
> &
    KitClearableProps<FieldPathEmptyValue<Values, Name>> & {
        readOnly?: boolean;
        size?: InputSize;
        allowNegative?: boolean;
        showCurrencySymbolIfEmpty?: boolean;
        selectOnFocus?: boolean;
    };

/**
 * Currency amount field bound to a typed Formik path. Emptying the input
 * writes `emptyValue` when the field is `clearable`; otherwise the form keeps
 * the last amount and the input shows it again on blur.
 */
export const MoneyField = <
    Values,
    Name extends MoneyFieldPath<Values> = MoneyFieldPath<Values>,
>(
    props: MoneyFieldProps<Values, Name>,
): ReactNode => {
    const {
        name,
        ref,
        disabled,
        readOnly,
        required,
        onValueChange,
        size,
        allowNegative,
        showCurrencySymbolIfEmpty,
        selectOnFocus,
    } = props;
    const clearTarget = toClearTarget(props);
    const [isShowingUnsavedEmpty, setIsShowingUnsavedEmpty] = useState(false);

    return (
        <FormikField<MoneyFieldValue> name={name} {...pickChromeProps(props)}>
            {({ field, helpers, invalid }) => {
                // react-number-format also reports prop-driven reformatting
                // (the "$ " prefix appearing), so equal values are not edits.
                const write = (
                    value: number | FieldPathEmptyValue<Values, Name>,
                ) => {
                    if (value === field.value) {
                        return;
                    }
                    void helpers.setValue(value);
                    onValueChange?.(value);
                };
                const handleValueChange = (value: number | undefined) => {
                    if (value !== undefined) {
                        setIsShowingUnsavedEmpty(false);
                        write(value);
                    } else if (clearTarget) {
                        write(clearTarget.emptyValue);
                    } else {
                        setIsShowingUnsavedEmpty(true);
                    }
                };

                return (
                    <MonetaryInput
                        ref={ref}
                        name={name}
                        value={
                            isShowingUnsavedEmpty
                                ? undefined
                                : (field.value ?? undefined)
                        }
                        onValueChange={handleValueChange}
                        onBlur={() => {
                            setIsShowingUnsavedEmpty(false);
                            void helpers.setTouched(true);
                        }}
                        invalid={invalid}
                        disabled={disabled}
                        readOnly={readOnly}
                        required={required}
                        aria-labelledby={props["aria-labelledby"]}
                        size={size}
                        allowNegative={allowNegative}
                        showCurrencySymbolIfEmpty={showCurrencySymbolIfEmpty}
                        selectOnFocus={selectOnFocus}
                    />
                );
            }}
        </FormikField>
    );
};
